/**
 * スライドエディタを提供します。
 */
import { temporaryId } from './temporaryId.js';

// @ts-ignore
const MTRichTextEditor = window.MTRichTextEditor;
// @ts-ignore
const mtappVars = window.mtappVars;
// @ts-ignore
const MT = window.MT;

export class SlideEditor {
    /**
     * SlideEditor クラスのインスタンスを生成します。
     *
     * @class SlideEditor
     *
     * @constructor
     * @param {string|HTMLElement} sourceField - SlideEditorを適用するソース要素またはそのID。
     * @param {Object} [options={}] - SlideEditorの設定オプション。
     * @param {string} [preset=''] - SlideEditorのプリセット設定文字列。（コンストラクタ内で現在は使用されていないが、将来の使用や拡張性のために保持されています。）
     * @param {Function|null} [onChange=null] - SlideEditorの内容が変更されたときに呼び出されるコールバック関数。
     */
    constructor(sourceField, options = {}, preset = '', onChange = null) {
        this.sourceElement = SlideEditor.getSourceElement(sourceField);
        if (!this.sourceElement) return;

        this.sourceElement.style.display = 'none';
        this.sourceElementId = SlideEditor.ensureElementHasId(this.sourceElement);

        mtappVars.__system.slideEditorIds = mtappVars.__system.slideEditorIds || [];
        if (!mtappVars.__system.slideEditorIds.includes(this.sourceElementId)) {
            mtappVars.__system.slideEditorIds.push(this.sourceElementId);
        }

        const existingButton = document.getElementById(`${this.sourceElementId}-button`);
        if (existingButton) {
            this.buttonElement = existingButton;
        } else {
            this.buttonElement = SlideEditor.createButtonElement(this.sourceElementId);
            this.sourceElement.parentNode.insertBefore(this.buttonElement, this.sourceElement.nextElementSibling);
        }
        this.buttonElement.addEventListener('click', async () => {
            await this.open();
        });

        const existingViewer = document.getElementById(`${this.sourceElementId}-view`);
        if (existingViewer) {
            this.viewerElement = existingViewer;
        } else {
            this.viewerElement = SlideEditor.createViewerElement(this.sourceElementId, this.sourceElement.value);
            this.sourceElement.parentNode.insertBefore(this.viewerElement, this.sourceElement.nextElementSibling);
        }

        // presetとoptionsの保存
        this.preset = preset;
        this.options = options;
        
        if (preset !== '' && typeof SlideEditor.presets[preset] !== 'undefined') {
            this.config = { ...SlideEditor.presets[preset] };
        } else if (Object.keys(options).length > 0) {
            this.config = { ...options };
        }
        if (Object.keys(this.config).length > 0) {
            this.config.selector = `#${SlideEditor.editorId}`;
        }

        this.onChange = onChange;
        if (this.sourceElement) {
            this.sourceElement._slideEditorInstance = this;
        }
    }

    //--------------------------------------------------
    // 静的プロパティ
    //--------------------------------------------------

    /** @static @property {string} editorId - エディタのID */
    static editorId = 'mtapp-slide-editor';

    /** @static @property {HTMLElement|null} editor - エディタのDOM要素 */
    static editor = null;

    /** @static @property {HTMLElement|null} editorContainer - エディタのコンテナのDOM要素 */
    static editorContainer = null;

    /** @static @property {HTMLElement|null} editorCancelButton - エディタのキャンセルボタンのDOM要素 */
    static editorCancelButton = null;

    /** @static @property {HTMLElement|null} editorSaveButton - エディタの保存ボタンのDOM要素 */
    static editorSaveButton = null;

    /** @static @property {HTMLElement|null} editorFooter - エディタのフッターのDOM要素 */
    static editorFooter = null;

    /** @static @property {string} currentSourceElementId - 現在のソース要素のID */
    static currentSourceElementId = '';

    /** @static @property {HTMLElement|null} currentSourceElement - 現在のソース要素 */
    static currentSourceElement = null;

    /** @static @property {HTMLElement|null} currentViewerElement - 現在のビューア要素 */
    static currentViewerElement = null;

    /** @static @property {Object|null} defaultEditor - デフォルトエディタのインスタンス */
    static defaultEditor = null;

    /**
     * @static
     * @property {Object} presets - エディタのプリセットの設定
     * @property {Object} presets.simple - シンプルなエディタの設定
     */
    static presets = {
        simple: {
            toolbar: [
                [
                    [
                        ['bold', 'italic'],
                        ['link', 'unlink'],
                        ['bulletList', 'orderedList']
                    ]
                ]
            ]
            // heightは指定しない（全画面で使用するため）
        }
    };

    //--------------------------------------------------
    // インスタンスのプロパティ
    //--------------------------------------------------

    /** @property {string} sourceElementId - ソース要素のID */
    sourceElementId = '';

    /** @property {HTMLElement|null} sourceElement - ソース要素 */
    sourceElement = null;

    /** @property {HTMLElement|null} viewerElement - ビューア要素 */
    viewerElement = null;

    /** @property {HTMLElement|null} buttonElement - エディタを開くボタンの要素 */
    buttonElement = null;

    /** @property {Object} config - インスタンスの設定 */
    config = {};

    /** @property {string} preset - プリセット名 */
    preset = '';

    /** @property {Object} options - カスタムオプション */
    options = {};

    /** @property {Function|null} onChange - 変更時のコールバック */
    onChange = null;

    //--------------------------------------------------
    // 静的メソッド
    //--------------------------------------------------

    /**
     * エディタを初期化
     */
    static init() {
        if (typeof mtappVars.__system.slideEditorReady === 'boolean' && mtappVars.__system.slideEditorReady === true) {
            return;
        }

        let editor = document.getElementById(SlideEditor.editorId);
        if (!editor) {
            // HTML構造を作成
            const html = `
            <div id="mtapp-slide-editor-container" style="height: 100vh; display: none; flex-direction: column;">
                <textarea id="mtapp-slide-editor" class="form-control" style="flex: 1; resize: none;"></textarea>
                <div id="mtapp-slide-footer" style="flex-shrink: 0; padding: 1rem; border-top: 1px solid #dee2e6; background: #f8f9fa;">
                    <button id="mtapp-slide-editor-save" type="button" class="btn btn-primary">${mtappVars.translate['Save Changes']}</button>
                    <button id="mtapp-slide-editor-cancel" type="button" class="btn btn-secondary ml-3">${mtappVars.translate['Cancel']}</button>
                </div>
            </div>
            <div id="mtapp-slide-editor-default-container" style="position: absolute; top: -1px; left: -1px; width: 1px; height: 1px; opacity: 0; pointer-events: none; overflow: hidden;">
                <textarea id="mtapp-slide-editor-default"></textarea>
            </div>
            `;

            // bodyタグの終了タグ直前にHTMLを挿入
            document.body.insertAdjacentHTML('beforeend', html.trim());
            editor = document.getElementById(SlideEditor.editorId);
        }

        // エディタ関係の要素を取得
        SlideEditor.editor = editor;
        SlideEditor.editorContainer = document.getElementById(`${SlideEditor.editorId}-container`);
        SlideEditor.editorCancelButton = document.getElementById(`${SlideEditor.editorId}-cancel`);
        SlideEditor.editorSaveButton = document.getElementById(`${SlideEditor.editorId}-save`);
        SlideEditor.editorFooter = document.getElementById('mtapp-slide-footer');

        // キャンセルボタンのイベントをセット
        SlideEditor.editorCancelButton.addEventListener('click', () => {
            SlideEditor.close();
        });

        // 保存ボタンのイベントをセット
        SlideEditor.editorSaveButton.addEventListener('click', () => {
            SlideEditor.save();
        });

        SlideEditor.defaultEditor = new MT.EditorManager(`${SlideEditor.editorId}-default`, {
            format: 'richtext',
        });
        
        // defaultEditorをmtappVarsに追加して外部からアクセス可能にする
        mtappVars.__system.slideEditorDefaultEditor = SlideEditor.defaultEditor;
        
        mtappVars.__system.SlideEditorReady = true;
    }

    /**
     * エディタのコンテンツを取得し Source Element に反映
     */
    static save() {
        SlideEditor.finishSourceMode();

        // エディタの内容を直接取得
        const editorContent = MTRichTextEditor.get({ id: SlideEditor.editorId }).getContent();
        SlideEditor.currentSourceElement.value = editorContent;
        SlideEditor.currentSourceElement.dispatchEvent(new Event('input'));
        SlideEditor.currentSourceElement.dispatchEvent(new Event('blur'));
        SlideEditor.currentViewerElement.innerHTML = editorContent;

        // コールバック呼び出し
        if (
            SlideEditor.currentSourceElement &&
            SlideEditor.currentSourceElement._slideEditorInstance &&
            typeof SlideEditor.currentSourceElement._slideEditorInstance.onChange === 'function'
        ) {
            SlideEditor.currentSourceElement._slideEditorInstance.onChange(editorContent);
        }

        SlideEditor.close('save');
    }

    /**
     * エディタを閉じる
     *
     * @param type
     */
    static close(type = 'cancel') {
        if (type === 'cancel') {
            SlideEditor.finishSourceMode();
        }

        // MTRichTextEditorを破棄
        document.body.classList.remove('mtapp-has-slide-editor');
        try {
            MTRichTextEditor.unload({ id: SlideEditor.editorId });
        } catch (error) {
            console.error('Error destroying editor:', error);
        }
        
        // mtappVarsのSlideEditor参照もクリア
        if (mtappVars.__system.currentSlideEditor) {
            delete mtappVars.__system.currentSlideEditor;
        }

        // エディタ要素の内容をクリア
        if (SlideEditor.editor) {
            SlideEditor.editor.value = '';
            // エディタUIも確実に削除
            const editorContainer = SlideEditor.editor.parentElement;
            const existingEditorUI = editorContainer.querySelector('.ProseMirror-menubar-wrapper');
            if (existingEditorUI) {
                existingEditorUI.remove();
            }
        }

        SlideEditor.cleanupEditorContainer();

        SlideEditor.currentSourceElementId = '';
        SlideEditor.currentSourceElement = null;
        SlideEditor.editorContainer.classList.remove('active');

        // サイドバーとbodyのoverflowを復元
        const sidebarCollapse = document.getElementById('mtapp-sidebar-collapse');
        if (sidebarCollapse) {
            setTimeout(() => {
                sidebarCollapse.style.display = 'block';
            }, 300);
        }
        document.body.style.overflow = '';

        // アニメーション完了後に非表示にする
        setTimeout(() => {
            SlideEditor.editorContainer.style.display = 'none';
        }, 300); // CSSのtransition時間と合わせる

        SlideEditor.addStyle('mtapp-slide-editor-style', '');
    }

    /**
     * Source Element を取得
     *
     * @param {HTMLElement|String} target
     * @returns {HTMLElement|null}
     */
    static getSourceElement(target) {
        if (target instanceof HTMLElement) {
            return target;
        } else if (typeof target === 'string') {
            return document.getElementById(target);
        }
        return null;
    }

    /**
     * Source Element の ID を取得、なければ一時的な ID を付与
     *
     * @param {HTMLElement} element
     * @returns {string}
     */
    static ensureElementHasId(element) {
        let id = element.getAttribute('id');
        if (!id) {
            id = temporaryId();
            element.setAttribute('id', id);
        }
        return id;
    }

    /**
     * Viewer Element を作成
     *
     * @param {string} id
     * @param {string} value
     * @returns {HTMLDivElement}
     */
    static createViewerElement(id, value) {
        const viewer = document.createElement('div');
        viewer.setAttribute('id', `${id}-view`);
        viewer.classList.add('mtapp-slide-editor-viewer');
        viewer.innerHTML = value;
        return viewer;
    }

    /**
     * エディタを開くボタンを作成
     *
     * @param {string} id
     * @returns {HTMLButtonElement}
     */
    static createButtonElement(id) {
        const button = document.createElement('button');
        button.setAttribute('id', `${id}-button`);
        button.setAttribute('type', 'button');
        button.classList.add('btn', 'btn-default', 'mt-3');
        button.innerText = '編集';
        return button;
    }

    /**
     * スタイルを追加
     *
     * @param {string} id
     * @param {string} cssContent
     */
    static addStyle(id, cssContent) {
        let style = document.getElementById(id);
        if (!style) {
            style = document.createElement('style');
            style.id = id;
            document.head.appendChild(style);
        }
        style.textContent = cssContent;
    }

    /**
     * エディタコンテナ内の不要な要素を除去
     */
    static cleanupEditorContainer() {
        if (!SlideEditor.editorContainer) {
            return;
        }

        const selectors = [
            'mt-rich-text-editor',
            '.mt-rich-text-editor',
            '.mt-rich-text-editor-editor',
            '.mt-rich-text-editor-toolbar',
            '.mt-rich-text-editor-content-root',
            '.mt-rich-text-editor-statusbar',
            '.ProseMirror-menubar-wrapper',
            '.ProseMirror'
        ];

        /**
         * @param {HTMLElement} element
         * @returns {boolean}
         */
        const shouldSkip = (element) => {
            if (!element) {
                return true;
            }
            if (SlideEditor.editor && (element === SlideEditor.editor || element.contains(SlideEditor.editor))) {
                return true;
            }
            if (SlideEditor.editorFooter && SlideEditor.editorFooter.contains(element)) {
                return true;
            }
            return false;
        };

        for (const selector of selectors) {
            const elements = SlideEditor.editorContainer.querySelectorAll(selector);
            for (const element of elements) {
                if (shouldSkip(element)) {
                    continue;
                }
                element.remove();
            }
        }

        const textNodes = Array.from(SlideEditor.editorContainer.childNodes).filter(
            (node) => node.nodeType === Node.TEXT_NODE && !node.textContent?.trim()
        );
        for (const node of textNodes) {
            node.remove();
        }

        if (!SlideEditor.editor || !SlideEditor.editorContainer.contains(SlideEditor.editor)) {
            const textarea = document.createElement('textarea');
            textarea.id = SlideEditor.editorId;
            textarea.classList.add('form-control');
            textarea.style.flex = '1';
            textarea.style.resize = 'none';
            SlideEditor.editorContainer.insertBefore(textarea, SlideEditor.editorFooter || null);
            SlideEditor.editor = textarea;
        }

        if (SlideEditor.editor) {
            SlideEditor.editor.style.display = '';
            SlideEditor.editor.style.flex = '1';
            SlideEditor.editor.style.resize = 'none';
            SlideEditor.editor.classList.add('form-control');
        }
    }

    /**
     * ソースモードを強制終了
     *
     * @static
     * @returns {void} 何も返しません
     */
    static finishSourceMode() {
        const sourceButtons = SlideEditor.editorContainer.querySelectorAll('[data-toolbar-option="source"]');
        for (const button of sourceButtons) {
            if (button.getAttribute('aria-pressed') === 'true' || button.classList.contains('active')) {
                button.click();
                return;
            }
        }
    }

    //--------------------------------------------------
    // インスタンスのメソッド
    //--------------------------------------------------

    /**
     * エディタを開く
     */
    async open(sourceElementId = null, content = null) {
        // 引数が渡された場合の処理（後方互換性のため）
        if (sourceElementId && content !== null) {
            this.sourceElementId = sourceElementId;
            this.sourceElement = SlideEditor.getSourceElement(sourceElementId);
            if (this.sourceElement) {
                this.sourceElement.value = content;
            }
        }
        SlideEditor.currentSourceElementId = this.sourceElementId;
        SlideEditor.currentSourceElement = this.sourceElement;
        SlideEditor.editor.value = this.sourceElement.value;
        SlideEditor.currentViewerElement = this.viewerElement;
        // エディタコンテナを表示してからアニメーション開始
        SlideEditor.editorContainer.style.display = 'flex';

        // サイドバーを非表示にして、bodyのoverflowを制御
        const sidebarCollapse = document.getElementById('mtapp-sidebar-collapse');
        if (sidebarCollapse) {
            sidebarCollapse.style.display = 'none';
        }
        document.body.style.overflow = 'hidden';

        // 少し遅延してからactiveクラスを追加してスライドイン
        setTimeout(() => {
            SlideEditor.editorContainer.classList.add('active');
            document.body.classList.add('mtapp-has-slide-editor');
        }, 10);

        // MT本体のデフォルトエディタのオプションを取得
        let defaultOptions = null;
        if (SlideEditor.defaultEditor?.currentEditor?.editor?.options) {
            defaultOptions = SlideEditor.defaultEditor.currentEditor.editor.options;
        }

        try {
            // 既存のエディタインスタンスを破棄
            if (MTRichTextEditor.get({ id: SlideEditor.editorId })) {
                try {
                    MTRichTextEditor.unload({ id: SlideEditor.editorId });
                    // エディタコンテナの内容をクリア（二重表示を防ぐ）
                    await new Promise(resolve => setTimeout(resolve, 100));
                } catch (error) {
                    console.warn('Failed to unload existing editor:', error);
                }
            }

            // エディタ要素の内容を完全にクリア
            if (SlideEditor.editor) {
                SlideEditor.editor.value = this.sourceElement.value;
                // 既存のエディタUIを削除
                const editorContainer = SlideEditor.editor.parentElement;
                const existingEditorUI = editorContainer.querySelector('.ProseMirror-menubar-wrapper');
                if (existingEditorUI) {
                    existingEditorUI.remove();
                }
            }

            SlideEditor.cleanupEditorContainer();
            if (SlideEditor.editor) {
                SlideEditor.editor.value = this.sourceElement.value;
            }

            // スライドエディターのコンテナ内での高さを計算
            const containerHeight = SlideEditor.editorContainer.offsetHeight;
            const footerHeight = SlideEditor.editorFooter.offsetHeight;
            const editorHeight = containerHeight - footerHeight;

            // MT管理画面のコンテキスト情報を取得
            const blogInput = document.querySelector('input[name="blog_id"]');
            const entryInput = document.querySelector('input[name="id"]');
            const blogId =
                (blogInput instanceof HTMLInputElement ? blogInput.value : null) ||
                // @ts-ignore
                window.CMSScriptContext?.blogId ||
                mtappVars.blog_id ||
                '';
            const entryId =
                (entryInput instanceof HTMLInputElement ? entryInput.value : null) ||
                // @ts-ignore
                window.CMSScriptContext?.objectId ||
                '';

            // MTRichTextEditorの基本設定を作成
            let toolbar = [
                [
                    [
                        ['bold', 'italic', 'underline', 'strike'],
                        ['blockquote', 'bulletList', 'orderedList', 'horizontalRule'],
                        ['link', 'unlink'],
                        ['insertHtml', 'file', 'image'],
                        ['table']
                    ],
                    [['source']]
                ],
                [
                    [
                        ['undo', 'redo'],
                        ['foregroundColor', 'backgroundColor', 'removeFormat'],
                        ['alignLeft', 'alignCenter', 'alignRight'],
                        ['indent', 'outdent'],
                        ['block']
                    ]
                ]
            ];
            if (defaultOptions?.toolbar && defaultOptions?.toolbar.length > 0) {
                toolbar = defaultOptions.toolbar;
            } else {
                const settingsElement = document.querySelector('[data-mt-rich-text-editor-settings]');
                if (settingsElement instanceof HTMLElement) {
                    const settings = JSON.parse(settingsElement.dataset.mtRichTextEditorSettings);
                    toolbar = settings.toolbar;
                }
            }

            /**
             * fullScreen要素を再帰的に削除する関数
             * @param {any} arr
             * @returns {any}
             */
            const removeFullScreen = (arr) => {
                if (Array.isArray(arr)) {
                    return arr.map((item) => {
                        if (Array.isArray(item)) {
                            return removeFullScreen(item);
                        }
                        return item;
                    }).filter((item) => {
                        if (Array.isArray(item)) {
                            return item.length > 0;
                        }
                        return item !== 'fullScreen';
                    });
                }
                return arr;
            };

            // toolbarが有効な配列であることを確認
            if (toolbar && Array.isArray(toolbar)) {
                // toolbarからfullScreen要素を削除
                toolbar = removeFullScreen(toolbar);
            }

            let editorConfig = {
                id: SlideEditor.editorId,
                height: editorHeight
            };

            // toolbarが有効な場合のみ設定
            if (toolbar && Array.isArray(toolbar) && toolbar.length > 0) {
                editorConfig.toolbar = toolbar;
            }

            // toolbarOptionsが存在する場合のみ設定
            if (defaultOptions?.toolbarOptions) {
                editorConfig.toolbarOptions = defaultOptions.toolbarOptions;
            }

            /**
             * TinyMCE形式のツールバーをMTRichTextEditor形式に変換
             * @param {string} toolbarString
             * @returns {Array<Array<Array<string>>> | null}
             */
            const convertTinyMCEToolbar = (toolbarString) => {
                if (!toolbarString || typeof toolbarString !== 'string') {
                    return null;
                }
                
                // TinyMCEのボタン名をMTRichTextEditorのボタン名にマッピング
                const buttonMap = {
                    'bold': 'bold',
                    'italic': 'italic',
                    'underline': 'underline',
                    'strikethrough': 'strike',
                    'link': 'link',
                    'unlink': 'unlink',
                    'forecolor': 'foregroundColor',
                    'backcolor': 'backgroundColor',
                    'blockquote': 'blockquote',
                    'bullist': 'bulletList',
                    'numlist': 'orderedList',
                    'alignleft': 'alignLeft',
                    'aligncenter': 'alignCenter',
                    'alignright': 'alignRight',
                    'alignjustify': 'alignJustify',
                    'outdent': 'outdent',
                    'indent': 'indent',
                    'code': 'source',
                    'undo': 'undo',
                    'redo': 'redo',
                    'removeformat': 'removeFormat',
                    'hr': 'horizontalRule',
                    'table': 'table',
                    'image': 'image',
                    'media': 'file'
                };
                
                // ツールバー文字列を解析
                const groups = toolbarString.split('|').map(group => group.trim());
                const converted = [];
                
                for (const group of groups) {
                    const buttons = group.split(/\s+/).filter(btn => btn);
                    const convertedButtons = [];
                    
                    for (const button of buttons) {
                        if (buttonMap[button]) {
                            convertedButtons.push(buttonMap[button]);
                        }
                    }
                    
                    if (convertedButtons.length > 0) {
                        converted.push(convertedButtons);
                    }
                }
                
                // MTRichTextEditor形式のツールバー配列を返す
                return converted.length > 0 ? [[converted]] : null;
            };

            // プリセットまたはカスタム設定がある場合
            if (this.preset !== '' && typeof SlideEditor.presets[this.preset] !== 'undefined') {
                // プリセット設定を適用
                const presetConfig = SlideEditor.presets[this.preset];
                if (presetConfig.toolbar) {
                    editorConfig.toolbar = presetConfig.toolbar;
                }
                // プリセットでheightが指定されていない場合は、計算したeditorHeightを使用
                if (presetConfig.height && presetConfig.height !== 300) {
                    editorConfig.height = presetConfig.height;
                }
                // その他のプリセット設定も適用
                for (const key in presetConfig) {
                    if (key !== 'height') {
                        editorConfig[key] = presetConfig[key];
                    }
                }
            } else if (Object.keys(this.config).length > 0) {
                // カスタムオプションを適用
                
                // TinyMCE形式のtoolbar文字列の場合は変換
                if (this.config.toolbar && typeof this.config.toolbar === 'string') {
                    const convertedToolbar = convertTinyMCEToolbar(this.config.toolbar);
                    if (convertedToolbar) {
                        editorConfig.toolbar = convertedToolbar;
                    }
                } else if (this.config.toolbar && Array.isArray(this.config.toolbar)) {
                    editorConfig.toolbar = this.config.toolbar;
                }
                
                if (this.config.height) {
                    editorConfig.height = this.config.height;
                }
                
                // その他の設定（language, menubarなど）は無視（MTRichTextEditorでは不要）
            }

            // コンテキスト情報を設定（アセット挿入に必要）
            if (blogId) {
                // @ts-ignore
                window.CMSScriptContext = window.CMSScriptContext || {};
                // @ts-ignore
                window.CMSScriptContext.blogId = blogId;
                if (entryId) {
                    // @ts-ignore
                    window.CMSScriptContext.objectId = entryId;
                }
            }

            console.log('Creating MTRichTextEditor with config:', editorConfig);
            const editorInstance = await MTRichTextEditor.create(editorConfig);

            // MT本体との互換性のためにmt-editorデータを設定
            // @ts-ignore
            jQuery('#' + SlideEditor.editorId).data('mt-editor', {
                currentEditor: editorInstance,
                editor: editorInstance
            });
            
            // 現在のエディタインスタンスをmtappVarsに保存
            mtappVars.__system.currentSlideEditor = editorInstance;

            // 内容変更時の自動同期
            // editorInstance.tiptap.on('update', () => {
            //     const content = editorInstance.getContent();
            //     SlideEditor.currentSourceElement.value = content;
            // });
        } catch (error) {
            console.error('Failed to create MTRichTextEditor:', error);
        }
    }
}
